/**
 * dispatchMarker — THE ONE READER of the engine's `dispatchesWorkflow` marker.
 *
 * The marker is the only static record of which CHILD agents a node dispatches
 * (the dispatch itself is compiled into the node's execute closure, so there is
 * nothing else to introspect). Since agent-workflow 2026-08-22 it serializes as
 * a STRING **or an ARRAY OF STRINGS**:
 *
 *   • STRING  — a declarative `{ workflow: 'slug' }` sub-graph node. Unchanged;
 *               every existing graph still serializes exactly this way.
 *   • ARRAY   — a node that fans out over a DECLARED ROSTER inside its own
 *               execute (board-runner's `dispatch`: N members, one
 *               `Promise.allSettled` over async `dispatchSubgraph` calls).
 *               One node, several children, one marker slot.
 *
 * Every reader in this app goes through here rather than testing
 * `typeof cfg.dispatchesWorkflow === 'string'`, because that test answers
 * "is this a dispatch node?" with NO for a roster node — which is how an array
 * marker silently turns into a garbled node badge, a dead `/agents/a,b,c` link,
 * and child runs that never attach to the step that spawned them.
 *
 * This is a deliberate per-repo COPY of the same three-line contract the
 * backend (`services/workflow-composition.dispatchedSlugs`), the CLI and the
 * self-host dashboard each hold: those four codebases share no module, and a
 * fake shared layer would be worse than four small honest ones. The contract is
 * pinned by tests on each side.
 */

/**
 * The child slugs one node's marker declares.
 *
 * @param {unknown} marker — a node config's `dispatchesWorkflow` value.
 * @returns {string[]} ordered, de-duplicated, trimmed slugs ([] when none).
 */
export function dispatchedSlugsOf(marker) {
  if (typeof marker === 'string') {
    const slug = marker.trim();
    return slug ? [slug] : [];
  }
  if (!Array.isArray(marker)) return [];
  const out = [];
  const seen = new Set();
  for (const raw of marker) {
    const slug = typeof raw === 'string' ? raw.trim() : '';
    if (!slug || seen.has(slug)) continue;
    seen.add(slug);
    out.push(slug);
  }
  return out;
}

/**
 * Which of a graph's nodes dispatch children, and which children each declares:
 * `{ nodeId: [childSlug, ...] }`, nodes that dispatch nothing omitted.
 *
 * The value is a LIST, not a slug, precisely because a roster node declares
 * several — a `{nodeId: slug}` map could only record one of them, which is how
 * every child run of such a node ended up unattached from the step that spawned
 * it.
 *
 * @param {object|null|undefined} nodeConfigs — a serialized graph's nodeConfigs.
 * @returns {Record<string, string[]>}
 */
export function dispatchNodeMapOf(nodeConfigs) {
  const map = {};
  if (!nodeConfigs || typeof nodeConfigs !== 'object') return map;
  for (const [nodeId, cfg] of Object.entries(nodeConfigs)) {
    const slugs = dispatchedSlugsOf(cfg?.dispatchesWorkflow);
    if (slugs.length) map[nodeId] = slugs;
  }
  return map;
}

/**
 * THE NAME a member satellite prints for the agent it points at.
 *
 * A slug is an id, not a word the product uses: the fleet whose members render
 * as `code-fix` / `ticket-triage` has already named them "Developer" / "Triage"
 * on their own pages, in the sidebar and on their deployed rows — the picture
 * simply never read the name. Internal vocabulary on a customer surface is a
 * defect, so the satellite prints the name and keeps the slug in its tooltip
 * (it is what somebody pastes into a support thread).
 *
 * ONE rule, no table: the DEPLOYED row's own name — which is a user's rename
 * when they made one, and the template's declared displayName otherwise (the
 * deploy stamps it) — else the slug, which is never blank. A child we could not
 * read (not installed, request failed) falls back rather than inventing a name.
 *
 * @param {object|null|undefined} row  the child's deployed workflow row
 * @param {string} slug  the declared child slug
 * @returns {string}
 */
export function childAgentName(row, slug) {
  const fallback = String(slug ?? '');
  const name = row && typeof row.displayName === 'string' ? row.displayName.trim() : '';
  return name || fallback;
}

export default dispatchedSlugsOf;
